import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
});

// ── Auth helpers ──
// JWT hook (002_auth.sql) injects user_role + user_email into claims
export async function getCurrentUser() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    if (typeof window !== 'undefined') window.__currentUserRole = null;
    return null;
  }
  const payload = JSON.parse(atob(session.access_token.split('.')[1]));
  // Lookup full_name from profile (small query, cached by PostgREST if called repeatedly)
  let name = session.user.email;
  try {
    const { data } = await supabase.from('user_profiles')
      .select('full_name').eq('id', session.user.id).maybeSingle();
    if (data?.full_name) name = data.full_name;
  } catch (_) { /* silent */ }
  const role = payload.user_role || 'viewer';
  // v28f: Expose role globally for iframe-loaded BOD pages to apply role-based UI
  if (typeof window !== 'undefined') window.__currentUserRole = role;
  return {
    id: session.user.id,
    email: payload.user_email || session.user.email,
    name,
    role
  };
}

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  await supabase.auth.signOut();
  window.location.hash = '#/login';
  window.location.reload();
}

export function onAuthChange(cb) {
  return supabase.auth.onAuthStateChange((event, session) => cb(event, session));
}

// ── RPC wrappers ──
export async function rpc(fn, args = {}) {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data;
}

// Core approval workflow — signatures match 004_rpc.sql
export const nextDocNumber     = (prefix) => rpc('next_doc_number', { p_prefix: prefix });
export const submitForApproval = (table, id) => rpc('submit_for_approval', { p_table: table, p_id: id });
export const approveRecord     = (table, id, level) => rpc('approve_record', { p_table: table, p_id: id, p_level: level });
export const rejectRecord      = (table, id, reason) => rpc('reject_record', { p_table: table, p_id: id, p_reason: reason });

// v8 parity — 006_v9_fixes.sql
export const checkBudget       = (maHD) => rpc('check_budget_by_hd', { p_ma_hd: maHD });
export const xuatKho           = (maVatTu, khoiLuong, noiDung, lyDo) => rpc('xuat_kho_v8', {
  p_ma_vat_tu: maVatTu, p_so_luong: khoiLuong, p_noi_dung: noiDung, p_ly_do: lyDo
});

// Dashboards
export const getIFRSDashboard  = (year) => rpc('get_ifrs_dashboard', { p_year: year || null });
export const getFPADashboard   = (maDuAn) => rpc('get_fpa_dashboard', { p_ma_du_an: maDuAn || null });
export const getPycPending     = () => rpc('get_pyc_cash_and_ungquy_pending', {});

// Admin
export const adminListUsers    = () => rpc('admin_list_users', {});
export const adminUpdateUser   = (userId, role, isActive) => rpc('admin_update_user_role', {
  p_user_id: userId, p_role: role, p_is_active: isActive
});

// ── Expose for non-module BOD scripts (in /public) ──
// BOD dashboards (bod-overview.html, pl-ifrs.html) load /bod-supabase.js as IIFE script,
// which can't import ES modules. They read from window globals instead.
if (typeof window !== 'undefined') {
  window.__supabase = supabase;
  window.__getCurrentUser = getCurrentUser;
}
